import React, { useRef } from 'react'
import css from './HeaderTabs.module.css'

export type HeaderTab<T extends string> = {
  value: T
  label: string
}

type Props<T extends string> = {
  tabs: HeaderTab<T>[]
  value: T
  onChange: (value: T) => void
  /** Describes the tab list for screen readers, e.g. "Members" */
  label: string
  /** Shown on the right, on the tabs' baseline (e.g. a count) */
  aside?: React.ReactNode
  /** A row below the tabs (e.g. search and sort) */
  toolbar?: React.ReactNode
}

/**
 * Section heading that doubles as tabs: each tab is an h3-sized title; the
 * selected one is black, the rest gray. Arrow keys move between tabs.
 */
const HeaderTabs = <T extends string>({
  tabs,
  value,
  onChange,
  label,
  aside,
  toolbar,
}: Props<T>) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = (index + step + tabs.length) % tabs.length
    onChange(tabs[next].value)
    buttonRefs.current[next]?.focus()
  }

  return (
    <div className={css['header']}>
      <div className={css['title-row']}>
        <div className={css['tabs']} role="tablist" aria-label={label}>
          {tabs.map((tab, index) => {
            const isSelected = tab.value === value
            return (
              <button
                key={tab.value}
                ref={(el) => (buttonRefs.current[index] = el)}
                type="button"
                role="tab"
                aria-selected={isSelected}
                // Only the selected tab is in the Tab order; arrows move between
                tabIndex={isSelected ? 0 : -1}
                className={`${css['tab']} ${isSelected ? css['selected'] : ''}`}
                onClick={() => onChange(tab.value)}
                onKeyDown={(e) => onKeyDown(e, index)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
        {aside && <div className={css['aside']}>{aside}</div>}
      </div>
      {toolbar && <div className={css['toolbar']}>{toolbar}</div>}
    </div>
  )
}

export default HeaderTabs
